import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, User } from "lucide-react";
import { api } from "@/lib/api";
import { ChatThread } from "@/components/ChatThread";

const Item = ({ active, onClick, icon: Icon, title, sub, testId }) => (
  <button data-testid={testId} onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 text-left border-b border-zinc-100 transition-colors duration-150 ${active ? "bg-[#0F3D3E] text-white" : "hover:bg-zinc-50"}`}>
    <Icon className="w-4 h-4 shrink-0" />
    <span className="min-w-0">
      <span className="block text-sm font-semibold truncate">{title}</span>
      {sub && <span className={`block font-mono text-[10px] uppercase tracking-widest truncate ${active ? "text-zinc-400" : "text-zinc-500"}`}>{sub}</span>}
    </span>
  </button>
);

export default function AdminChat() {
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState(params.get("class") ? "class" : "user");
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const sel = params.get("user") || params.get("class");

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/classes").then((r) => setClasses(r.data));
  }, []);

  const pickUser = (id) => setParams({ user: id });
  const pickClass = (c) => setParams({ class: c });
  const selUser = users.find((u) => u.id === params.get("user"));
  const selClass = params.get("class");
  const sorted = useMemo(() => [...users].sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || "")), [users]);

  return (
    <div>
      <p className="font-mono text-xs uppercase tracking-widest text-[#0D9488] mb-3">Live messaging</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-gradient tracking-tight mb-10">Chat interna</h1>
      <div className="grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 bg-white border border-zinc-200 h-[70vh] flex flex-col">
          <div className="grid grid-cols-2 border-b border-zinc-200" data-testid="chat-channel-select">
            {[["user", "Diretti", User], ["class", "Per classe", Users]].map(([m, l, I]) => (
              <button key={m} data-testid={`chat-mode-${m}`} onClick={() => setMode(m)} className={`flex items-center justify-center gap-2 h-12 text-sm font-semibold transition-colors duration-150 ${mode === m ? "bg-[#0F3D3E] text-white" : "hover:bg-zinc-50"}`}>
                <I className="w-4 h-4" /> {l}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto">
            {mode === "user" ? sorted.map((u) => (
              <Item key={u.id} testId={`chat-user-${u.id}`} active={selUser?.id === u.id} onClick={() => pickUser(u.id)} icon={User} title={`${u.nome} ${u.cognome}`} sub={`${u.classe} · ${u.istituto}`} />
            )) : classes.map((c) => (
              <Item key={c} testId={`chat-class-${c}`} active={selClass === c} onClick={() => pickClass(c)} icon={Users} title={`Classe ${c}`} sub={`${users.filter((u) => u.classe === c).length} studenti`} />
            ))}
            {(mode === "user" ? users : classes).length === 0 && <p className="p-6 text-sm text-zinc-400">Nessun elemento</p>}
          </div>
        </aside>
        <section className="lg:col-span-8">
          {selUser ? (
            <ChatThread key={`u-${selUser.id}`} fetchUrl={`/admin/messages/user/${selUser.id}`} toUserId={selUser.id} title={`${selUser.nome} ${selUser.cognome}`} subtitle={`Chat diretta · ${selUser.classe}`} />
          ) : selClass ? (
            <ChatThread key={`c-${selClass}`} fetchUrl={`/admin/messages/class/${selClass}`} classTarget={selClass} title={`Classe ${selClass}`} subtitle="Messaggio di gruppo a tutta la classe" />
          ) : (
            <div className="h-[70vh] border border-dashed border-zinc-300 grid place-items-center text-center p-8" data-testid="chat-empty-state">
              <div>
                <p className="font-display text-xl font-bold mb-2">Seleziona una conversazione</p>
                <p className="text-sm text-zinc-500">Scegli uno studente o una classe {sel ? "" : "dalla lista"}.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
